import { BigNumber } from 'bignumber.js';
// In node js, maximumFractionDigits is 20 for some reason
// In safari maximumFractionDigits greater than 20 throws an out of range error
const MAX_FRACTIONAL_DIGITS = 20;
export const formatParts = (input, format) => {
    const { compactDecimalZeroes, compactDecimalZeroesThreshold = 5, compactDecimalZeroesRemainingPartLength = 4, maximumSignificantDigits, mantissa, trimMantissa = true, prefix, currency, forceSign = false, roundingMode = 'halfExpand', } = format;
    const bn = BigNumber(input);
    const prefixParts = (() => {
        const result = [];
        if (prefix) {
            result.push({ type: 'literal', value: prefix });
        }
        if (bn.isNegative()) {
            result.push({ type: 'minusSign', value: '-' });
        }
        if (forceSign && bn.isPositive()) {
            result.push({ type: 'plusSign', value: '+' });
        }
        if (currency && currency === '$') {
            result.push({ type: 'currency', value: '$' });
        }
        return result;
    })();
    const trailingParts = (() => {
        const result = [];
        if (currency && currency !== '$') {
            result.push({ type: 'literal', value: ' ' }, { type: 'currency', value: currency });
        }
        return result;
    })();
    if (format.compact) {
        const formatter = new Intl.NumberFormat('en-US', {
            notation: 'compact',
            compactDisplay: 'short',
            maximumFractionDigits: mantissa ?? 1,
            minimumFractionDigits: 0,
            maximumSignificantDigits: maximumSignificantDigits,
            roundingMode,
            signDisplay: 'never',
        });
        const parts = formatter.formatToParts(bn.toNumber());
        return [...prefixParts, ...parts, ...trailingParts];
    }
    const formatDecimal = (bn) => {
        const resolvedMantissa = Math.min(mantissa ?? 2, MAX_FRACTIONAL_DIGITS);
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'decimal',
            notation: 'standard',
            maximumFractionDigits: resolvedMantissa,
            minimumFractionDigits: trimMantissa ? 0 : resolvedMantissa,
            maximumSignificantDigits: maximumSignificantDigits,
            roundingMode,
            signDisplay: 'never',
        });
        const parts = formatter.formatToParts(bn.toString());
        return [...prefixParts, ...parts, ...trailingParts];
    };
    if (!compactDecimalZeroes) {
        return formatDecimal(bn);
    }
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        notation: 'standard',
        maximumFractionDigits: MAX_FRACTIONAL_DIGITS,
        minimumFractionDigits: trimMantissa ? 0 : MAX_FRACTIONAL_DIGITS,
        signDisplay: 'never',
    });
    const parts = formatter.formatToParts(bn.toString());
    const fractionalPartIndex = parts.findIndex((it) => it.type === 'fraction');
    if (fractionalPartIndex === -1) {
        return formatDecimal(bn);
    }
    const fractionalPart = parts.at(fractionalPartIndex);
    // Count leading zeros in the fractional part
    const fractionalLeadingZerosMatch = /^0+/.exec(fractionalPart.value);
    const fractionalLeadingZeros = fractionalLeadingZerosMatch
        ? fractionalLeadingZerosMatch[0]
        : '';
    if (fractionalLeadingZeros.length < compactDecimalZeroesThreshold) {
        return formatDecimal(bn);
    }
    // Get the remaining fractional part after leading zeros
    const fractionalRemainingPartString = fractionalPart.value.replace(/^0+/, '');
    const fractionalRemainingPartBn = BigNumber(`0.${fractionalRemainingPartString}`).decimalPlaces(compactDecimalZeroesRemainingPartLength);
    const trimmedFractionalRemainingPartString = fractionalRemainingPartBn
        .toFormat({
        groupSeparator: '',
        fractionGroupSeparator: '',
        decimalSeparator: '.',
    })
        .replace('0.', '');
    const compactParts = [
        ...parts.slice(0, fractionalPartIndex),
        { type: 'fraction', value: '0' },
        {
            type: 'compact-decimal-zeroes',
            value: fractionalLeadingZeros.length.toString(),
        },
        { type: 'fraction', value: trimmedFractionalRemainingPartString },
    ];
    return [...prefixParts, ...compactParts, ...trailingParts];
};
export const format = (input, format) => {
    const parts = formatParts(input, format);
    return formatPartsToString(parts);
};
export const formatPartsToString = (parts) => {
    return parts.map(stringifyPart).join('');
};
export const stringifyPart = (part) => {
    if (part.type === 'compact-decimal-zeroes') {
        return `(${part.value})`;
    }
    return part.value;
};
//# sourceMappingURL=numeric-format-base.js.map